import dontGetVolunteered from "../../../assets/img/blog/googleFoobarChallange/3.3-dont-get-volunteered.png";
import {Player} from "@lottiefiles/react-lottie-player";
import dontGetVolunteered1 from "../../../assets/json/3.3-dont-get-volunteered.json";
import dontGetVolunteered2 from "../../../assets/json/3.3-dont-get-volunteered-2.json";
import dontGetVolunteered3 from "../../../assets/json/3.3-dont-get-volunteered-3.json";
import {getRandomColor} from "../../../utils/GetRandomColor";
import IconText from "../../IconText";
import {faHand} from "@fortawesome/free-solid-svg-icons";

export default function DontGetVolunteered() {
    const lottieStyle = {
        border: `5px solid ${getRandomColor()}`,
    };

    const imageStyle = {
        border: `5px solid ${getRandomColor()}`,
    };

    return (
        <div className={"blog-body"}>
            <h1><IconText text={"3.3. Don't Get Volunteered!"} icon={faHand}/></h1>

            <h2>Question</h2>
            <div className={"blog-quote"}>
                <p>
                    As a henchman on Commander Lambda's space station, you're expected to be resourceful, smart, and
                    a quick
                    thinker. It's not easy building a doomsday device and ordering the bunnies around at the same
                    time,
                    after all! In order to make sure that everyone is sufficiently quick-witted, Commander Lambda
                    has
                    installed new flooring outside the henchman dormitories. It looks like a chessboard, and every
                    morning
                    and evening you have to solve a new movement puzzle in order to cross the floor. That would be
                    fine if
                    you got to be the rook or the queen, but instead, you have to be the knight. Worse, if you take
                    too much
                    time solving the puzzle, you get "volunteered" as a test subject for the LAMBCHOP doomsday
                    device!
                </p>
                <p>
                    To help yourself get to and from your bunk every day, write a function called solution(src,
                    dest) which
                    takes in two parameters: the source square, on which you start, and the destination square,
                    which is
                    where you need to land to solve the puzzle. The function should return an integer representing
                    the
                    smallest number of moves it will take for you to travel from the source square to the
                    destination square
                    using a chess knight's moves (that is, two squares in any direction immediately followed by one
                    square
                    perpendicular to that direction, or vice versa, in an "L" shape). Both the source and
                    destination
                    squares will be an integer between 0 and 63, inclusive, and are numbered like the example
                    chessboard
                    below:
                </p>
                <p>
                    ------------------------- <br/>
                    | 0| 1| 2| 3| 4| 5| 6| 7| <br/>
                    ------------------------- <br/>
                    | 8| 9|10|11|12|13|14|15| <br/>
                    ------------------------- <br/>
                    |16|17|18|19|20|21|22|23| <br/>
                    ------------------------- <br/>
                    |24|25|26|27|28|29|30|31| <br/>
                    ------------------------- <br/>
                    |32|33|34|35|36|37|38|39| <br/>
                    ------------------------- <br/>
                    |40|41|42|43|44|45|46|47| <br/>
                    ------------------------- <br/>
                    |48|49|50|51|52|53|54|55| <br/>
                    ------------------------- <br/>
                    |56|57|58|59|60|61|62|63| <br/>
                    ------------------------- <br/>
                </p>
                <p>
                    <b>Test cases</b>
                </p>
                <p>
                    Input: <br/>
                    solution.solution(0, 1) <br/>
                    Output: <br/>
                    3 <br/>
                    <br/>
                    Input: <br/>
                    solution.solution(19, 36) <br/>
                    Output: <br/>
                    1 <br/>
                </p>
            </div>

            <h2>Solution</h2>
            <p>
                I have played a lot of chess. I mean, a lot. After reading the question, I grabbed my chessboard and
                the
                knight piece and tried to figure out a pattern, if there was any. Then I came up with the following
                table:
            </p>

            <img src={dontGetVolunteered}
                 className={"blog-img"}
                 alt="Don't Get Volunteered"
                 style={imageStyle}
            />

            <p>
                What you are seeing here is the minimum number of moves to reach that square from the square that
                the knight
                is on. You may notice the size of the table is greater than 8x8, which is the size of a chessboard.
                This is
                because I thought it would be easier to move the board on this map instead of recalculating the
                moves every
                time with different knight placements.
            </p>

            <div className="blog-lottie-wrapper">
                <Player
                    style={lottieStyle}
                    src={dontGetVolunteered1} className={"blog-lottie"}
                    autoplay
                    loop
                    background={"#e9e8e6"}>
                </Player>
            </div>

            <p>
                Then I noticed an exception in the pattern. If the knight is placed on the corner, the nearest
                corner
                becomes reachable after 4 moves.
            </p>

            <div className="blog-lottie-wrapper">
                <Player
                    style={lottieStyle}
                    src={dontGetVolunteered2} className={"blog-lottie"}
                    autoplay
                    loop
                    background={"#e9e8e6"}>
                </Player>
            </div>

            <p>
                And another exception is If the knight is placed on one diagonal away from the corner, the closest
                corner
                square becomes reachable in 4 moves.
            </p>

            <div className="blog-lottie-wrapper">
                <Player
                    style={lottieStyle}
                    src={dontGetVolunteered3} className={"blog-lottie"}
                    autoplay
                    loop
                    background={"#e9e8e6"}>
                </Player>
            </div>

            <p>
                Changing the grid according to these exceptions, I wrote the <code>solution</code> function. You
                will also
                notice the helper
                functions <code>get_directions</code>, <code>is_valid_move</code>, <code>move</code>, and
                <code>navigator</code> in the code. These are used to find the directions from the source to the
                destination
                and to move the knight on the grid. I am certain that there are more efficient ways to solve the
                navigation
                part,
                but I didn't want to dwell on it too much.
            </p>

            <h2>Code</h2>
            <pre><code className="language-python">{`grid = [
[6,5,4,5,4,5,4,5,4,5,4,5,4,5,6,],
[5,4,5,4,3,4,3,4,3,4,3,4,5,4,5,],
[4,5,4,3,4,3,4,3,4,3,4,3,4,5,4,],
[5,4,3,4,3,2,3,2,3,2,3,4,3,4,5,],
[4,3,4,3,2,3,2,3,2,3,2,3,4,3,4,],
[5,4,3,2,3,4,1,2,1,4,3,2,3,4,5,],
[4,3,4,3,2,1,2,3,2,1,2,3,4,3,4,],
[5,4,3,2,3,2,3,0,3,2,3,2,3,4,5,],
[4,3,4,3,2,1,2,3,2,1,2,3,4,3,4,],
[5,4,3,2,3,4,1,2,1,4,3,2,3,4,5,],
[4,3,4,3,2,3,2,3,2,3,2,3,4,3,4,],
[5,4,3,4,3,2,3,2,3,2,3,4,3,4,5,],
[4,5,4,3,4,3,4,3,4,3,4,3,4,5,4,],
[5,4,5,4,3,4,3,4,3,4,3,4,5,4,5,],
[6,5,4,5,4,5,4,5,4,5,4,5,4,5,6,],
]

def get_directions(start, end, grid_size=8):
    """
    Returns the directions from start to end in the grid.
    """
    # Calculate row and column for start and end
    start_row, start_col = divmod(start, grid_size)
    end_row, end_col = divmod(end, grid_size)

    # Calculate the number of steps in each direction
    steps_right = end_col - start_col
    steps_down = end_row - start_row

    # Generate the directions based on the steps
    directions = (
        "R" * max(steps_right, 0)
        + "L" * max(-steps_right, 0)
        + "D" * max(steps_down, 0)
        + "U" * max(-steps_down, 0)
    )

    return directions

def is_valid_move(x, y, grid):
    """
    Returns True if the move is valid, False otherwise.
    """
    return 0 <= x < len(grid) and 0 <= y < len(grid[0])

def move(x, y, direction):
    """
    Returns the new position after moving in the given direction.
    """
    directions = {'U': (-1, 0), 'D': (1, 0), 'L': (0, -1), 'R': (0, 1)}
                    dx, dy = directions[direction]
    new_x, new_y = x + dx, y + dy
    return new_x, new_y

def navigator(path, grid):
    """
    Returns the value of the destination cell after following the given path.
    """
    current_x, current_y = 7, 7  # Starting position

    for move_direction in path:
        current_x, current_y = move(current_x, current_y, move_direction)
        if not is_valid_move(current_x, current_y, grid):
            return None  # Invalid move, return None

    return grid[current_x][current_y]

def solution(src, dest):
    # There are few exceptions where grid is changed depending on the knight's position

    # The knight being in the corners
    if src in [0, 7, 56, 63]:
        grid[6][6] = 4
        grid[6][8] = 4
        grid[8][6] = 4
        grid[8][8] = 4

    # The knight being in one diagonal away from the corners
    elif src == 9:
        grid[6][6] = 4

    elif src == 14:
        grid[6][8] = 4

    elif src == 49:
        grid[8][6] = 4

    elif src == 54:
        grid[8][8] = 4

    else:
        pass

    # Finding directions from src to dest
    directions = get_directions(src, dest)

    min_moves = navigator(directions, grid)

    return min_moves`}</code></pre>
        </div>
    );
}